// src/file-size-in-octet/index.ts
function fileSizeInOctet(mo = 0, ko = 0, o = 0) {
  return mo * 1048576 + ko * 1024 + o;
}

// src/format-date/index.ts
function formatDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date).replace(",", "");
}

// src/format-phone/index.ts
import parsePhoneNumberFromString from "libphonenumber-js";
function formatPhone(phoneNumber) {
  if (!phoneNumber) {
    return "";
  }
  const phone = parsePhoneNumberFromString(phoneNumber.toString(), {
    defaultCountry: "FR",
    extract: false
  });
  return phone?.formatInternational();
}

// src/format-ticks-short-label/index.ts
function formatTicksToShortLabel(ticks) {
  const ms = ticks / 1e4;
  const totalSeconds = ms / 1e3;
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor(totalSeconds % 86400 / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = +(totalSeconds % 60).toFixed(0);
  return [
    days ? `${days}j` : "",
    hours ? `${hours}h` : "",
    minutes ? `${minutes}min` : "",
    seconds ? `${seconds}sec` : ""
  ].filter(Boolean).join(" ");
}

// src/genere-unique-identifier/index.ts
function generateUniqueIdentifier(prefix = "") {
  const date = /* @__PURE__ */ new Date();
  return prefix ? `${prefix}-${date.getTime()}` : date.getTime().toString();
}

// src/hooks/use-click-outside.ts
import { useCallback, useRef } from "react";
function useClickOutside(onOutsideClick) {
  const ref = useRef(null);
  const handleClick = useCallback(
    (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick(event);
      }
    },
    [onOutsideClick]
  );
  const setRef = useCallback(
    (node) => {
      if (ref.current) {
        document.removeEventListener("mousedown", handleClick);
      }
      if (node) {
        document.addEventListener("mousedown", handleClick);
      }
      ref.current = node;
    },
    [handleClick]
  );
  return setRef;
}

// src/hooks/use-clipboard.ts
import { useCallback as useCallback2, useState } from "react";
function useClipboard(resetDelay = 2e3) {
  const [{ copied, error }, setState] = useState({
    copied: false,
    error: null
  });
  const resetState = useCallback2(
    () => setState({ copied: false, error: null }),
    []
  );
  const copyToClipboard = useCallback2(
    async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        setState({ copied: true, error: null });
        if (resetDelay > 0) {
          setTimeout(resetState, resetDelay);
        }
      } catch (error2) {
        setState({ copied: false, error: error2 });
        console.error("Failed to copy text to clipboard:", error2);
      }
    },
    [resetDelay, resetState]
  );
  return { copyToClipboard, resetState, copied, error };
}

// src/hooks/use-debounce-callback.ts
import { useCallback as useCallback3, useEffect, useRef as useRef2 } from "react";
function useDebounceCallback(callback, delay) {
  const timeoutRef = useRef2(null);
  const callbackRef = useRef2(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
  const debounced = useCallback3(
    (...args) => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
  return debounced;
}

// src/hooks/use-permissions.ts
import { useMemo } from "react";
var USER_ROLES = {
  OPPY: "Oppy",
  USER: "User",
  ADMIN: "Admin"
};
var RESOURCES = {
  RCS_SETTING: "RCSSetting",
  RCS_AGENT: "RCSAgent",
  RCS_CAMPAIGN: "RCSCampaign",
  SALES_COMPLIANT: "salesCompliant",
  QUALITY_MONITORING: "qualityMonitoring"
};
var PERMISSIONS = {
  READ: "read",
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  ACCESS: "access"
};
var PERMISSIONS_FULL_ACCESS = [
  PERMISSIONS.ACCESS,
  PERMISSIONS.READ,
  PERMISSIONS.CREATE,
  PERMISSIONS.UPDATE,
  PERMISSIONS.DELETE
];
var CAPABILITY_MATRIX = {
  [USER_ROLES.OPPY]: {
    [RESOURCES.RCS_SETTING]: PERMISSIONS_FULL_ACCESS,
    [RESOURCES.RCS_AGENT]: PERMISSIONS_FULL_ACCESS,
    [RESOURCES.RCS_CAMPAIGN]: PERMISSIONS_FULL_ACCESS,
    [RESOURCES.SALES_COMPLIANT]: [PERMISSIONS.ACCESS],
    [RESOURCES.QUALITY_MONITORING]: [PERMISSIONS.ACCESS]
  },
  [USER_ROLES.USER]: {
    [RESOURCES.RCS_SETTING]: [
      PERMISSIONS.ACCESS,
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE
    ],
    [RESOURCES.RCS_AGENT]: [],
    [RESOURCES.RCS_CAMPAIGN]: [],
    [RESOURCES.SALES_COMPLIANT]: [],
    [RESOURCES.QUALITY_MONITORING]: []
  },
  [USER_ROLES.ADMIN]: {
    [RESOURCES.RCS_SETTING]: PERMISSIONS_FULL_ACCESS,
    [RESOURCES.RCS_AGENT]: PERMISSIONS_FULL_ACCESS,
    [RESOURCES.RCS_CAMPAIGN]: [PERMISSIONS.ACCESS, PERMISSIONS.READ],
    [RESOURCES.SALES_COMPLIANT]: [],
    [RESOURCES.QUALITY_MONITORING]: []
  }
};
var OPPY_ONLY_RESOURCES = [
  RESOURCES.SALES_COMPLIANT,
  RESOURCES.QUALITY_MONITORING
];
var RCS_RESOURCES = [
  RESOURCES.RCS_SETTING,
  RESOURCES.RCS_AGENT,
  RESOURCES.RCS_CAMPAIGN
];
function usePermissions(currentUser) {
  const capabilities = useMemo(() => {
    if (!currentUser) return null;
    return CAPABILITY_MATRIX[currentUser.role] ?? null;
  }, [currentUser]);
  const hasPermission = (resource, permission) => {
    if (!capabilities) return false;
    return capabilities[resource].includes(permission);
  };
  const getResourcePermissions = (resource) => {
    if (!capabilities) return [];
    return capabilities[resource];
  };
  const createPermissionChecker = (permission) => (resource) => hasPermission(resource, permission);
  const canRead = createPermissionChecker(PERMISSIONS.READ);
  const canCreate = createPermissionChecker(PERMISSIONS.CREATE);
  const canUpdate = createPermissionChecker(PERMISSIONS.UPDATE);
  const canDelete = createPermissionChecker(PERMISSIONS.DELETE);
  const canAccess = createPermissionChecker(PERMISSIONS.ACCESS);
  const canAccessRCS = () => RCS_RESOURCES.some((resource) => getResourcePermissions(resource).length > 0);
  const isOppyOnly = (resource) => OPPY_ONLY_RESOURCES.includes(
    resource
  ) || resource === RESOURCES.RCS_CAMPAIGN && currentUser?.role !== USER_ROLES.OPPY;
  const isAuthorized = (resource, permission) => hasPermission(resource, permission);
  return {
    hasPermission,
    getResourcePermissions,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canAccess,
    canAccessRCS,
    isOppyOnly,
    isAuthorized,
    userRole: currentUser?.role,
    capabilities
  };
}
var use_permissions_default = usePermissions;

// src/is-file-format-ok/index.ts
function isFileFormatOk(fileName, allowedFormats) {
  const fileExtension = fileName.split(".").pop()?.toLowerCase();
  return allowedFormats.includes(fileExtension || "");
}

// src/is-file-size-ok/index.ts
function isFileSizeOk(size, maxSize) {
  return size <= maxSize * 1024 * 1024;
}

// src/is-valid-aspect-ratio/index.ts
var isValidAspectRatio = (width, height, allowedRatios) => allowedRatios.some((r) => Math.abs(width / height - r) < 0.1);
var is_valid_aspect_ratio_default = isValidAspectRatio;

// src/percentage-with-precision/index.ts
var defaultConfig = {
  precision: 2
};
var percentageWithPrecision = (totalCount, config = defaultConfig) => (value) => Number(
  (value * 100 / (totalCount || 1) * Number(!!totalCount)).toFixed(
    config.precision
  )
);
var percentage_with_precision_default = percentageWithPrecision;

// src/random/index.ts
function random(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

// src/remove-duplicates/index.ts
function removeDuplicates(array) {
  return [...new Set(array)];
}

// src/simulate-latency/index.ts
function simulateLatency() {
  const delay = Math.floor(Math.random() * (3e3 - 1e3 + 1) + 1e3);
  return new Promise((resolve) => setTimeout(resolve, delay));
}

// src/sort-by-key/index.ts
function sortByKey(array, key, sortDirection = "asc") {
  return array.slice().sort((a, b) => {
    const getValue = (obj, path) => path.split(".").reduce((value, key2) => value?.[key2], obj);
    const valueA = getValue(a, key);
    const valueB = getValue(b, key);
    const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    return sortDirection === "asc" ? comparison : -comparison;
  });
}
export {
  PERMISSIONS,
  RESOURCES,
  USER_ROLES,
  fileSizeInOctet,
  formatDate,
  formatPhone,
  formatTicksToShortLabel,
  generateUniqueIdentifier,
  isFileFormatOk,
  isFileSizeOk,
  is_valid_aspect_ratio_default as isValidAspectRatio,
  percentage_with_precision_default as percentageWithPrecision,
  random,
  removeDuplicates,
  simulateLatency,
  sortByKey,
  useClickOutside,
  useClipboard,
  useDebounceCallback,
  use_permissions_default as usePermissions
};
